import { Injectable } from '@angular/core';
import { REPORT_STEP_DEFINITIONS, reportConfig } from '../pages/reports/model/report-create/reports.config';
import { ReportFlowContext, ReportFlowStep } from '../types/dtos/reports.dto';
import { ReportSteps } from '../types/enums/report.enum';
import { StepperStepInterface } from '../types/interfaces/stepper.interface';

@Injectable({
  providedIn: 'root',
})
export class ReportFlowService {

  private _context: ReportFlowContext = {};

  private _currentStep: ReportFlowStep | null = null;

  /**
   * The steps the user actually visited.
   */
  private _history: ReportFlowStep[] = [];

  /**
   * The complete calculated flow for the
   * current context.
   */
  private _steps: ReportFlowStep[] = [];

  // --------------------------------------------------------------------------
  // START
  // --------------------------------------------------------------------------

  public start(
    context: ReportFlowContext = {},
  ): void {

    this._context = {
      ...context,
    };

    this._history = [];

    this._steps =
      this._buildFlow();

    this._currentStep =
      this._steps[0] ?? null;
  }

  // --------------------------------------------------------------------------
  // CONTEXT
  // --------------------------------------------------------------------------

  public setContext(
    context: Partial<ReportFlowContext>,
  ): void {

    this._context = {
      ...this._context,
      ...context,
    };

    /*
     * Rebuild the possible flow because
     * conditions may have changed.
     */
    this._steps =
      this._buildFlow();
  }

  public getContext(): ReportFlowContext {
    return {
      ...this._context,
    };
  }

  // --------------------------------------------------------------------------
  // CURRENT STEP
  // --------------------------------------------------------------------------

  public getCurrentStep():
    ReportSteps | null {

    return this._currentStep?.step ?? null;
  }

  public getCurrentStepperStep():
    StepperStepInterface | null {

    const step =
      this.getCurrentStep();

    if (step === null) {
      return null;
    }

    return REPORT_STEP_DEFINITIONS[step];
  }

  // --------------------------------------------------------------------------
  // ALL STEPS
  // --------------------------------------------------------------------------

  public getAllSteps():
    StepperStepInterface[] {

    return this._steps.map((step: ReportFlowStep,): StepperStepInterface =>
        REPORT_STEP_DEFINITIONS[step.step],
      );
  }

  // --------------------------------------------------------------------------
  // GO TO STEP
  // --------------------------------------------------------------------------

  public canGoToStep(
    step: ReportSteps,
  ): boolean {

    const index =
      this._steps.findIndex(
        (
          item: ReportFlowStep,
        ): boolean =>
          item.step === step,
      );

    if (index === -1) {
      return false;
    }

    const currentIndex =
      this._getCurrentIndex();

    /*
     * Cannot jump forward.
     *
     * The user has to complete the
     * previous steps first.
     */
    if (index > currentIndex + 1) {
      return false;
    }

    return true;
  }

  public goToStep(
    step: ReportSteps,
  ): boolean {

    if (!this.canGoToStep(step)) {
      return false;
    }

    const index =
      this._steps.findIndex(
        (
          item: ReportFlowStep,
        ): boolean =>
          item.step === step,
      );

    if (index === -1) {
      return false;
    }

    const currentIndex =
      this._getCurrentIndex();

    /*
     * Going backwards.
     */
    if (index < currentIndex) {

      this._history =
        this._history.slice(
          0,
          index,
        );

      this._currentStep =
        this._steps[index];

      return true;
    }

    /*
     * Going to the next step.
     */
    if (index === currentIndex + 1) {

      return this.next();
    }

    /*
     * Already on this step.
     */
    return true;
  }

  // --------------------------------------------------------------------------
  // NEXT
  // --------------------------------------------------------------------------

  public next(): boolean {

    if (!this._currentStep) {
      return false;
    }

    const currentIndex =
      this._getCurrentIndex();

    const nextStep =
      this._steps[
      currentIndex + 1
        ];

    if (!nextStep) {
      return false;
    }

    this._history.push(
      this._currentStep,
    );

    this._currentStep =
      nextStep;

    return true;
  }

  // --------------------------------------------------------------------------
  // BACK
  // --------------------------------------------------------------------------

  public back(): boolean {

    if (!this._history.length) {
      return false;
    }

    const previousStep =
      this._history.pop();

    if (!previousStep) {
      return false;
    }

    this._currentStep =
      previousStep;

    return true;
  }

  // --------------------------------------------------------------------------
  // CAN BACK / NEXT
  // --------------------------------------------------------------------------

  public canGoBack(): boolean {
    return this._history.length > 0;
  }

  public canGoNext(): boolean {

    if (!this._currentStep) {
      return false;
    }

    const currentIndex =
      this._getCurrentIndex();

    return Boolean(
      this._steps[
      currentIndex + 1
        ],
    );
  }

  // --------------------------------------------------------------------------
  // RESET
  // --------------------------------------------------------------------------

  public reset(): void {

    this._context = {};

    this._history = [];

    this._steps = [];

    this._currentStep = null;
  }

  // --------------------------------------------------------------------------
  // PRIVATE
  // --------------------------------------------------------------------------

  private _getCurrentIndex(): number {

    if (!this._currentStep) {
      return -1;
    }

    return this._steps.findIndex(
      (
        step: ReportFlowStep,
      ): boolean =>
        step.step ===
        this._currentStep?.step,
    );
  }

  /**
   * Converts the tree-based config into
   * the actual linear flow.
   */
  private _buildFlow():
    ReportFlowStep[] {

    const result: ReportFlowStep[] = [];

    let current:
      ReportFlowStep | null =
      reportConfig[0] ?? null;

    while (current) {

      result.push(current);

      current =
        this._getNextStep(current);
    }

    return result;
  }

  private _getNextStep(
    current: ReportFlowStep,
  ): ReportFlowStep | null {

    if (!current.next?.length) {
      return null;
    }

    return (
      current.next.find(
        (
          step: ReportFlowStep,
        ): boolean =>
          !step.when ||
          step.when(
            this._context,
          ),
      ) ?? null
    );
  }
}
